"use client";

/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * useTabStateWithUrl
 *
 * Manages tab state synchronized with URL search parameters.
 * Provides deep linking, browser history support, and tab validation.
 *
 * @example
 * ```tsx
 * const allowedTabs = ["overview", "estimates", "activity"];
 * const { activeTab, setActiveTab } = useTabStateWithUrl({
 *   allowedTabs,
 *   defaultTab: "overview",
 *   paramName: "tab",
 * });
 * ```
 */

import { logger } from "@/lib/utils/logger";
import { useSearchParams } from "next/navigation";
import { useNavigationWithProgress } from "@/hooks/utilities/use-navigation-with-progress";
import { useCallback, useMemo, useState } from "react";

export interface UseTabStateWithUrlOptions {
  /**
   * Array of valid tab values. Used for validation.
   */
  allowedTabs: readonly string[];
  /**
   * Default tab value if no tab is specified in URL or invalid tab is provided.
   */
  defaultTab: string;
  /**
   * URL search parameter name for the tab state. Defaults to "tab".
   */
  paramName?: string;
  /**
   * Whether to enable URL synchronization. Defaults to true.
   */
  enabled?: boolean;
}

export interface UseTabStateWithUrlReturn {
  /**
   * Current active tab value.
   */
  activeTab: string;
  /**
   * Function to change the active tab. Updates both state and URL.
   */
  setActiveTab: (tab: string) => void;
  /**
   * Handler for Tabs component's onValueChange prop.
   */
  onTabChange: (value: string) => void;
}

/**
 * Hook to manage tab state synchronized with URL search parameters.
 *
 * Features:
 * - Deep linking to specific tabs
 * - Browser back/forward support
 * - Tab validation against allowed list
 * - URL synchronization
 *
 * @param options - Configuration options
 * @returns Tab state and handlers
 */
export function useTabStateWithUrl({
  allowedTabs,
  defaultTab,
  paramName = "tab",
  enabled = true,
}: UseTabStateWithUrlOptions): UseTabStateWithUrlReturn {
  const router = useNavigationWithProgress();
  const searchParams = useSearchParams();

  // Get initial tab from URL or use default
  const initialTab = useMemo(() => {
    if (!enabled) return defaultTab;
    const urlTab = searchParams?.get(paramName);
    if (urlTab && allowedTabs.includes(urlTab)) {
      return urlTab;
    }
    return defaultTab;
  }, [searchParams, paramName, allowedTabs, defaultTab, enabled]);

  const [activeTab, setActiveTabState] = useState<string>(initialTab);

  // Update URL when tab changes
  const setActiveTab = useCallback(
    (tab: string) => {
      // Validate tab
      if (!allowedTabs.includes(tab)) {
        logger.warn(
          `Invalid tab "${tab}". Must be one of: ${allowedTabs.join(", ")}`,
        );
        return;
      }

      setActiveTabState(tab);

      if (!enabled) return;

      // Update URL with new tab
      const sp = new URLSearchParams(searchParams?.toString() || "");
      sp.set(paramName, tab);
      router.replace(`?${sp.toString()}`);
    },
    [allowedTabs, enabled, paramName, router, searchParams],
  );

  // Handler for Tabs component's onValueChange
  const onTabChange = useCallback(
    (value: string) => {
      setActiveTab(value);
    },
    [setActiveTab],
  );

  return useMemo(
    () => ({
      activeTab,
      setActiveTab,
      onTabChange,
    }),
    [activeTab, setActiveTab, onTabChange],
  );
}
